import { useTranslation } from '@shared/core';
import { resolveIndexThemeId } from '@apps/card/utils/colorway';
import { usePrecomputedEmbeddings } from '@shared/utils/search';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';
import { useProductTypes } from '@apps/card/components/ProductUIConfig/useProductTypes';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { StationeryTemplate } from '@apps/card/types/stationeryTemplate.types';

/**
 * Turns an embeddings-index concept into a display word. The index packs synonyms
 * into one keyword ("fall fall autumn", "botanical leaves"), namespaces some
 * ("color: roseGold", "material: marble") and camel-cases others ("artDeco"),
 * so keep the first word after any namespace and split its camel case.
 */
const conceptLabel = (conceptName: string): string =>
  conceptName
    .replace(/^\w+: /, '')
    .split(' ')[0]
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();

const STANDALONE_PRODUCTS: ReadonlySet<string | undefined> = new Set(['napkin', 'sign', 'magnet']);

/** The tile's "{concepts} {category} {product}" alt text, e.g. "botanical minimal Save the Date Card". */
export const useTileAltText = (template: Pick<StationeryTemplate, 'themeId' | 'products'>, category: StationeryTemplateCategoryEnum): string => {
  const { tq } = useTranslation('stationery');
  const { themes } = usePrecomputedEmbeddings();
  const getCategoryName = useStationeryTemplateCategoryTranslations('stationeryTemplateCategoryToTile');
  const productTypes = useProductTypes();

  // Through the suffix rollup, so a `_foil` variant is described like its parent.
  const indexThemeId = themes ? resolveIndexThemeId(template.themeId, key => themes.has(key)) : undefined;
  const conceptNames = (indexThemeId === undefined ? undefined : themes?.get(indexThemeId)?.conceptNames) ?? [];
  // Named for its product only when that is a lone napkin, sign or magnet; anything else,
  // including a tile sold as several products (card, postcard, magnet), is a card.
  const [onlyProduct] = template.products.length === 1 ? template.products : [];
  const standaloneProductType = STANDALONE_PRODUCTS.has(onlyProduct?.productType) ? onlyProduct?.productType : undefined;
  const productLabel = productTypes.find(product => product.type === standaloneProductType)?.translations.galleryPriceStringName ?? tq`Card`;

  return [...conceptNames.map(conceptLabel), getCategoryName(category), productLabel].join(' ');
};
